import { useEffect, useReducer, useCallback } from 'react';
/**
 * Hook utilizado para hacer fetch como abstracción por encima de esta API,
 * que tiene en cuenta el paso de varias urls,
 *
 * la espera de cambio de datos mientras
 * se recibe el json y la posibiblidad de que se haya cancelado el fetch.
 */

export const useDataApi = (
  { initialUrl, initialData, initialUrlInfo = {}, log = true, cache = false },
  cb = null
) => {
  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: false,
    isError: false,
    data: initialData,
    url: initialUrl,
    requestedUrlInfo: initialUrlInfo,
    urlInfo: initialUrlInfo,
    cache: false,
    cb,
  });

  const cacheHeaders = {
    credentials: 'same-origin',
    cache: 'no-store',
  };

  const requestUrl = useCallback(
    (url, requestedUrlInfo) =>
      dispatch({
        type: 'SET_URL_INFO',
        requestedUrlInfo,
        url,
      }),
    []
  );

  const { url, requestedUrlInfo } = state;
  useEffect(() => {
    let didCancel = false;

    const fetchAll = () => {
      dispatch({ type: 'FETCH_INIT' });
      log && console.log(`Iniciado fetch de ${url}`);
      Promise.all(
        url.map(x =>
          fetch(x,cacheHeaders)
            .then(response => {
              if (!response.ok) {
                throw Error(response.statusText);
              }
              return response.json();
            })
            .catch(error => {
              console.error(`Error loading ${x}: ${error}`);
              return null; // Return null for failed fetches
            })
        )
      )
        .then(data => {
          if (!didCancel) {
            const successfulData = data.filter(item => item !== null); // Filter out null values (failed fetches)
            log &&
              console.log(
                `Loaded ${successfulData.length} out of ${url.length} URLs`
              );
            dispatch({
              type: 'FETCH_SUCCESS',
              data: successfulData,
              urlInfo: requestedUrlInfo,
            });
          }
        })
        .catch(error => {
          if (!didCancel) {
            console.error('Failed to fetch data from one or more URLs:', error);
            dispatch({ type: 'FETCH_FAILURE' });
          }
        });
    };

    const fetchData = () => {
      dispatch({ type: 'FETCH_INIT' });
      log && console.log(`Iniciado fetch de ${url}`);

      fetch(url, cacheHeaders)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText);
          }
          return response;
        })
        .then(response => response.json())
        .then(data => {
          if (!didCancel) {
            console.log(`Cargado ${url}`);
            dispatch({
              type: 'FETCH_SUCCESS',
              data,
              urlInfo: requestedUrlInfo,
            });
          }
        })
        .catch(error => {
          if (!didCancel) {
            log && console.error(`Fallo al cargar ${url}: ${error}`);
            dispatch({ type: 'FETCH_FAILURE' });
          }
        });
    };

    if (url != null) {
      if (Array.isArray(url)) {
        fetchAll();
      } else {
        fetchData();
      }
    }

    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, requestedUrlInfo, log]);

  return [state, requestUrl];
};

const dataFetchReducer = (state, action) => {
  const { type, ...rest } = action;
  switch (action.type) {
    case 'FETCH_INIT':
      return {
        ...state,
        isLoading: true,
        isError: false,
      };
    case 'FETCH_SUCCESS': {
      const { data, ...r } = rest;
      return {
        ...state,
        isLoading: false,
        isError: false,
        ...r,
        data: typeof state.cb === 'function' ? state.cb(data) : data,
      };
    }
    case 'FETCH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isError: true,
      };
    case 'SET_URL_INFO':
      return {
        ...state,
        ...rest,
      };
    default:
      throw new Error();
  }
};

export default useDataApi;
